/**
 * AI Tools Panel
 * Panel de herramientas IA para cursos existentes
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Badge,
  Progress,
} from "@learn-me/ui";
import { adminApi, type AITask, type AITaskAction } from "../api/adminApi";
import {
  Sparkles,
  Image,
  CheckCircle2,
  AlertTriangle,
  Search,
  Trash2,
  BarChart3,
  Loader2,
  RefreshCw,
  X,
  Clock,
  CheckCircle,
  XCircle,
  Pause,
} from "lucide-react";
import toast from "react-hot-toast";

interface AIToolsPanelProps {
  courseId: string;
}

const TASK_TYPE_ICONS: Record<string, React.ReactNode> = {
  GENERATE_DIAGRAMS: <Image className="h-4 w-4" />,
  VALIDATE_CONTENT: <Search className="h-4 w-4" />,
  CLEAN_CONTENT: <Trash2 className="h-4 w-4" />,
  ANALYZE_QUALITY: <BarChart3 className="h-4 w-4" />,
};

const TASK_STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  COMPLETED: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
  CANCELLED: "bg-gray-100 text-gray-800",
};

const TASK_STATUS_ICONS: Record<string, React.ReactNode> = {
  PENDING: <Clock className="h-3 w-3" />,
  PROCESSING: <Loader2 className="h-3 w-3 animate-spin" />,
  COMPLETED: <CheckCircle className="h-3 w-3" />,
  FAILED: <XCircle className="h-3 w-3" />,
  CANCELLED: <Pause className="h-3 w-3" />,
};

export function AIToolsPanel({ courseId }: AIToolsPanelProps) {
  const queryClient = useQueryClient();
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  // Fetch AI status for the course
  const { data: status, isLoading } = useQuery({
    queryKey: ["course-ai-status", courseId],
    queryFn: () => adminApi.getCourseAIStatus(courseId),
    refetchInterval: activeTaskId ? 2000 : false, // Poll while task is active
  });

  // Fetch active task details
  const { data: activeTask } = useQuery({
    queryKey: ["ai-task", activeTaskId],
    queryFn: () => adminApi.getAITask(activeTaskId!),
    enabled: !!activeTaskId,
    refetchInterval: 2000,
  });

  // Update active task ID when task completes
  if (
    activeTask &&
    (activeTask.status === "COMPLETED" ||
      activeTask.status === "FAILED" ||
      activeTask.status === "CANCELLED")
  ) {
    setActiveTaskId(null);
    queryClient.invalidateQueries({ queryKey: ["course-ai-status", courseId] });
  }

  // Mutations for each action type
  const generateDiagramsMutation = useMutation({
    mutationFn: () => adminApi.generateCourseDiagrams(courseId),
    onSuccess: (task) => {
      setActiveTaskId(task.id);
      toast.success("Generando diagramas...");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al iniciar la tarea");
    },
  });

  const validateContentMutation = useMutation({
    mutationFn: () => adminApi.validateCourseContent(courseId),
    onSuccess: (task) => {
      setActiveTaskId(task.id);
      toast.success("Validando contenido...");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al iniciar la tarea");
    },
  });

  const cleanContentMutation = useMutation({
    mutationFn: () => adminApi.cleanCourseContent(courseId),
    onSuccess: (task) => {
      setActiveTaskId(task.id);
      toast.success("Limpiando contenido...");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al iniciar la tarea");
    },
  });

  const analyzeQualityMutation = useMutation({
    mutationFn: () => adminApi.analyzeCourseQuality(courseId),
    onSuccess: (task) => {
      setActiveTaskId(task.id);
      toast.success("Analizando calidad...");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al iniciar la tarea");
    },
  });

  const cancelTaskMutation = useMutation({
    mutationFn: (taskId: string) => adminApi.cancelAITask(taskId),
    onSuccess: () => {
      setActiveTaskId(null);
      queryClient.invalidateQueries({
        queryKey: ["course-ai-status", courseId],
      });
      toast.success("Tarea cancelada");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al cancelar la tarea");
    },
  });

  const handleAction = (action: AITaskAction) => {
    switch (action.type) {
      case "GENERATE_DIAGRAMS":
        generateDiagramsMutation.mutate();
        break;
      case "VALIDATE_CONTENT":
        validateContentMutation.mutate();
        break;
      case "CLEAN_CONTENT":
        cleanContentMutation.mutate();
        break;
      case "ANALYZE_QUALITY":
        analyzeQualityMutation.mutate();
        break;
    }
  };

  const isPending =
    generateDiagramsMutation.isPending ||
    validateContentMutation.isPending ||
    cleanContentMutation.isPending ||
    analyzeQualityMutation.isPending;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Herramientas IA
          </h2>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          Herramientas IA
        </h2>
        <Button
          size="sm"
          variant="ghost"
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ["course-ai-status", courseId],
            })
          }
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Image className="h-3 w-3" />
              Diagramas
            </div>
            <div className="font-semibold">
              {status.stats.sectionsWithDiagrams}/{status.stats.totalSections}
            </div>
            <Progress
              value={status.stats.diagramCoverage}
              className="h-1 mt-1"
            />
          </div>
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <BarChart3 className="h-3 w-3" />
              Audio
            </div>
            <div className="font-semibold">
              {status.stats.sectionsWithAudio}/{status.stats.totalSections}
            </div>
            <Progress value={status.stats.audioCoverage} className="h-1 mt-1" />
          </div>
        </div>

        {/* Active Task */}
        {activeTask && activeTask.status === "PROCESSING" && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {activeTask.currentStep || "Procesando..."}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                onClick={() => cancelTaskMutation.mutate(activeTask.id)}
                disabled={cancelTaskMutation.isPending}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
            <Progress value={activeTask.progress} className="h-2" />
            {activeTask.processedItems !== null &&
              activeTask.totalItems !== null && (
                <p className="text-xs text-blue-600 mt-1">
                  {activeTask.processedItems} / {activeTask.totalItems}
                </p>
              )}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          {status.availableActions.map((action) => (
            <Button
              key={action.type}
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={() => handleAction(action)}
              disabled={!action.enabled || isPending || !!activeTaskId}
            >
              {TASK_TYPE_ICONS[action.type] || (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              <span className="ml-2 flex-1 text-left">{action.label}</span>
              {action.description && (
                <span className="text-xs text-muted-foreground">
                  {action.description}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Recent Tasks */}
        {status.recentTasks.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-2">
              Tareas recientes
            </h3>
            <div className="space-y-2">
              {status.recentTasks.slice(0, 3).map((task) => (
                <div
                  key={task.id}
                  className="flex items-center gap-2 text-sm p-2 bg-muted/30 rounded"
                >
                  {TASK_TYPE_ICONS[task.type] || (
                    <Sparkles className="h-3 w-3" />
                  )}
                  <span className="flex-1 truncate">
                    {getTaskLabel(task.type)}
                  </span>
                  <Badge
                    variant="outline"
                    className={`text-xs ${TASK_STATUS_COLORS[task.status]}`}
                  >
                    {TASK_STATUS_ICONS[task.status]}
                    <span className="ml-1">{getStatusLabel(task.status)}</span>
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Generated Badge */}
        {status.isAIGenerated && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground pt-2 border-t">
            <CheckCircle2 className="h-3 w-3 text-green-600" />
            Curso generado con IA
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getTaskLabel(type: string): string {
  const labels: Record<string, string> = {
    GENERATE_DIAGRAMS: "Generar diagramas",
    VALIDATE_CONTENT: "Validar contenido",
    CLEAN_CONTENT: "Limpiar contenido",
    ANALYZE_QUALITY: "Analizar calidad",
    REGENERATE_SECTION: "Regenerar sección",
    REGENERATE_CHAPTER: "Regenerar capítulo",
    REGENERATE_QUIZ: "Regenerar quiz",
    ENHANCE_QUIZ: "Mejorar quiz",
    GENERATE_AUDIO: "Generar audio",
  };
  return labels[type] || type;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    PENDING: "Pendiente",
    PROCESSING: "En proceso",
    COMPLETED: "Completado",
    FAILED: "Fallido",
    CANCELLED: "Cancelado",
  };
  return labels[status] || status;
}
